// Definir interfaces para mejorar la claridad y el manejo de tipos
interface IStorageManager {
  loadData(): Promise<void>;
  saveData(key: string, value: any): Promise<void>;
  getItem(key: string): any;
  setItem(key: string, value: any): void;
}

export class LocalStorageManager implements IStorageManager {
  public data: { [key: string]: any } = {};
  protected dbName: string;
  protected storeName: string;
  protected timesRegister: {[id:string]: number} = {}
  private dbVersion: number;
  public saving = false;

  constructor(dbName: string, storeName: string) {
    this.dbName = dbName;
    this.storeName = storeName;
    this.dbVersion = import.meta.env.VITE_APP_API_DBVERSION ?? 1
  }

  // Método para abrir una conexión a IndexedDB
  protected open(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onupgradeneeded = (event) => {
        const db = request.result;
        const stores = [
          "appConfigs",
          "vtPermissions",
          "vtEntities",
          "vtStore",
          "vtModules",
          "vtUser",
          "vtList",
          "vtModComments",
          "vtModTracker",
          "vtRelList",
          "vtFiles",
          "vtFilter",
        ]
        stores.map((store: string) => {
          if (db.objectStoreNames.contains(store)) db.deleteObjectStore(store);
          const st = db.createObjectStore(store, { keyPath: 'id' })
          st.createIndex('id', 'id', { unique: true });
        })
      };

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
    });
  }

  // Método para cargar datos de IndexedDB a memoria
  public async loadData(): Promise<any> {
    const db = await this.open();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(this.storeName, 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.getAll();

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.data = request.result?.reduce((data, item) => ({ ...data, [item.id]: item.value }), this.data)
        resolve(this.data as any)
      };
    });
  }
  // Método para obtener un elemento por ID directamente desde IndexedDB
  public async getItemById(id: string): Promise<any> {
    if(!id) return null; // Si no se proporciona un ID, retorna null
    const db = await this.open();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(this.storeName, 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.get(id);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const item = request.result ? request.result.value : null;
        if (!Object.isExtensible(this.data)) {
          this.data = { ...this.data };
        }
        this.data[id] = item;
        resolve(item);
      };
    });
  }

  // Método para guardar datos de memoria a IndexedDB
  public async saveData(key: string, value: any): Promise<void> {
    if (this.timesRegister[key] > (Date.now() - 1000)) return;
    if (this.saving) {
      await new Promise((resolve) => setTimeout(resolve, 25))
      await this.saveData(key, value)
      return 
    }
    this.timesRegister[key] = Date.now()
    const db = await this.open();
    this.saving = true
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(this.storeName, 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.put({ id: key, value });
      request.onerror = () => {
        this.saving = false
        reject(request.error);
      }
      request.onsuccess = () => {
        this.saving = false
        resolve();
      }
    });
  }

  // Método para obtener un valor de memoria
  public getItem(key: string): any {
    return this.data[key] ?? null;
  }

  // Método para establecer un valor en memoria y sincronizar con IndexedDB
  public setItem(key: string, value: any): void {
    if (!Object.isExtensible(this.data)) {
      this.data = { ...this.data };
    }
    this.data[key] = value;
    this.saveData(key, value).catch(console.error);
  }

  // Método para eliminar un elemento tanto de memoria como de IndexedDB
  public async removeItem(key: string): Promise<void> {
    delete this.data[key]; // Eliminar de memoria

    const db = await this.open();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(this.storeName, 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(key);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  // Método para eliminar la base de datos completamente y resetear los datos en memoria
  public async deleteDatabase(): Promise<void> {
    // Primero, limpia los datos en memoria para resetear el estado actual
    this.data = {};

    // Luego, elimina la base de datos IndexedDB
    return new Promise((resolve, reject) => {
      const request = indexedDB.deleteDatabase(this.dbName);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        console.log(`La base de datos '${this.dbName}' ha sido eliminada exitosamente.`);
        resolve();
      };
      request.onblocked = async () => {
        console.warn(`La solicitud para eliminar la base de datos '${this.dbName}' está bloqueada.`);
        // Aquí puedes manejar casos donde la eliminación está bloqueada, por ejemplo, notificando al usuario
        await this.removeItem('vtiger_current_user');
        await this.removeItem('system-config');
        await this.removeItem('vtiger_modules_info');
        await this.removeItem('vtiger_recents_views');
      };
    });
  }

}

export const storageManager = new LocalStorageManager(`${import.meta.env.VITE_APP_API_DBNAME}`, `${import.meta.env.VITE_APP_API_DBSTORE}`);
